import React from 'react';
import { motion } from 'framer-motion';
import { 
  Star, 
  Users, 
  RefreshCcw,
  ArrowUpRight,
  Sparkles
} from 'lucide-react';

const loyaltyActions = [
  {
    icon: Star,
    title: 'Review Requests',
    description: 'Automated review solicitation at peak satisfaction moments.',
    result: '+47% review rate'
  },
  {
    icon: Users,
    title: 'Referral Programs',
    description: 'Incentivized sharing that turns customers into advocates.',
    result: '3.2x referral growth'
  },
  {
    icon: RefreshCcw,
    title: 'Retention Campaigns',
    description: 'Re-engagement sequences that prevent churn and drive repeat purchases.',
    result: '89% retention rate'
  },
];

export default function LoyaltySection() {
  return (
    <section 
      id="loyalty"
      data-testid="loyalty-section"
      className="section-padding relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#0a0508] to-[#030303]" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-pink-400 mb-4 block">
            Stage 4 / Loyalty
          </span>
          <h2 
            data-testid="loyalty-headline"
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            The Growth
            <br />
            <span className="text-pink-400">Loop</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-base">
            Happy customers become your best marketing channel - generating referrals 
            that feed back into the awareness stage.
          </p>
        </motion.div>
        
        {/* Loyalty Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {loyaltyActions.map((action, index) => (
            <motion.div
              key={index}
              data-testid={`loyalty-card-${index}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.5 }}
              className="group"
            >
              <div className="glass rounded-2xl p-6 h-full card-hover border border-pink-500/10">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-pink-500/10 flex items-center justify-center group-hover:bg-pink-500/20 transition-colors">
                    <action.icon className="w-6 h-6 text-pink-400" />
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-pink-400/50 group-hover:text-pink-400 transition-colors" />
                </div>
                
                <h3 className="text-lg font-semibold text-white mb-2">
                  {action.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  {action.description}
                </p>
                
                <div className="pt-4 border-t border-white/10">
                  <span className="text-sm font-medium text-pink-400">{action.result}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Loop Visualization */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="flex justify-center"
        >
          <div className="relative w-64 h-64">
            {/* Circular path */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
              <motion.circle
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke="url(#loopGrad)"
                strokeWidth="2"
                strokeDasharray="8 8"
                initial={{ rotate: 0 }}
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                style={{ transformOrigin: 'center' }}
              />
              <defs>
                <linearGradient id="loopGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#ec4899" />
                  <stop offset="50%" stopColor="#7000FF" />
                  <stop offset="100%" stopColor="#00F0FF" />
                </linearGradient>
              </defs>
            </svg>
            
            {/* Center content */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                animate={{ 
                  scale: [1, 1.1, 1],
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="glass-heavy rounded-full p-6"
              >
                <Sparkles className="w-8 h-8 text-pink-400" />
              </motion.div>
            </div>
            
            {/* Floating particles */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-3 h-3 rounded-full bg-pink-400"
                style={{
                  left: '50%',
                  top: '50%',
                }}
                animate={{
                  x: [0, Math.cos(i * 60 * Math.PI / 180) * 90],
                  y: [0, Math.sin(i * 60 * Math.PI / 180) * 90],
                  opacity: [1, 0.5, 1],
                  scale: [1, 0.5, 1],
                }}
                transition={{
                  duration: 3,
                  delay: i * 0.5,
                  repeat: Infinity,
                }}
              />
            ))}
          </div>
        </motion.div>
        
        {/* Loop explanation */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="text-center text-gray-400 text-sm mt-8"
        >
          Referrals flow back into the <span className="text-cyan-400">Awareness</span> stage, 
          creating compounding growth
        </motion.p>
      </div>
    </section>
  );
}
